import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../../../util_components/button';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import RequiredReasonTextarea from '../../../util_components/required_reason_textarea';
import {
  CareRequestFields,
  CareSaveRetry,
  TeacherNoteField
} from '../../../util_components/care_request';
import {
  INTERNAL_NOTE_LABEL,
  INTERNAL_NOTE_PLACEHOLDER,
  KIND_TEACHER_CHANGE,
  build_create_payload,
  care_api_error,
  covers_care
} from '../../../util_components/care_request/care_request_rules';
import { post_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { api_error_message } from '../../../../utils/api_error_message';
import useCareCapture from './use_care_capture';
import {
  plan_teacher_change,
  teacher_change_payload,
  teacher_options_of
} from './session_change_rules';

// The session page's "Change Teacher" body; mounted per open. Care's details apply on every session type it covers.
function ChangeTeacherForm(props) {
  const { session_uuid, change_session_uuid, session_type, teachers } = props;
  const [teacher_uuid, setTeacher] = useState('');
  const [reason, setReason] = useState('');
  const [reason_error, setReasonError] = useState(false);
  const [busy, setBusy] = useState(false);
  // One swap per click: the ref closes the window before the state lands (upload + swap take seconds).
  const in_flight = useRef(false);
  const care = useCareCapture({
    session_uuid,
    kind: covers_care(KIND_TEACHER_CHANGE, session_type) ? KIND_TEACHER_CHANGE : null,
    on_saved: props.on_care_saved,
    on_retry_change: props.on_retry_change
  });

  const submit = async () => {
    const plan = plan_teacher_change({
      change_reason: reason,
      care_draft: care.draft,
      teacher_uuid,
      now_ms: Date.now()
    });
    if (plan.reason_error) {
      setReasonError(true);
      return;
    }
    if (plan.care_errors) {
      care.setErrors(plan.care_errors);
      return;
    }
    if (plan.teacher_missing || in_flight.current) return;
    in_flight.current = true;
    setBusy(true);
    const settle = () => {
      in_flight.current = false;
      setBusy(false);
    };
    let screenshot_key = '';
    try {
      screenshot_key = await care.upload_once();
    } catch (e) {
      settle();
      props.notify(
        'error',
        care_api_error(e, 'The screenshot could not be uploaded. Nothing was changed.').message
      );
      return;
    }
    // Submit stays disabled until the Care save settles: success via done, failure via the Retry dialog.
    const done = () => {
      settle();
      props.on_done();
    };
    const payload = teacher_change_payload({
      session_uuid: change_session_uuid,
      teacher_uuid,
      reason: plan.reason,
      care_draft: care.draft
    });
    post_api(get_api_url('change_teacher_for_session'), payload, true)
      .then(() => {
        if (!care.draft) {
          done();
          return;
        }
        care.save_after_change(
          build_create_payload(care.draft, {
            session_uuid: change_session_uuid,
            note: plan.reason,
            screenshot_key,
            expected_teacher_uuid: teacher_uuid
          }),
          done
        );
      })
      .catch((e) => {
        settle();
        props.notify('error', api_error_message(e));
      });
  };

  return (
    <>
      <h5>Change Teacher</h5>
      {props.warning ? (
        <p
          style={{
            margin: '10px 0',
            padding: '8px',
            maxWidth: '400px',
            color: 'var(--state-warning-text)',
            background: 'var(--state-warning-bg)'
          }}
        >
          {props.warning}
        </p>
      ) : null}
      {props.gender_caption ? (
        <p className="text-sm font-semibold text-content-muted">{props.gender_caption}</p>
      ) : null}
      <div style={{ margin: '10px 0' }}>
        <CustomSingleSelect
          label="Select a teacher"
          options={teacher_options_of(teachers)}
          onChange={(e) => setTeacher(e.target.value)}
          value={teacher_uuid || ''}
          style={{ width: '300px' }}
          data-session_id={change_session_uuid}
        />
      </div>
      <RequiredReasonTextarea
        value={reason || ''}
        onChange={(e) => {
          setReason(e.target.value);
          setReasonError(false);
        }}
        label={INTERNAL_NOTE_LABEL}
        placeholder={INTERNAL_NOTE_PLACEHOLDER}
        error={!!reason_error}
        errorText="An internal note (at least 3 characters) is required to change the teacher"
        height="60px"
      />
      {care.draft ? (
        <TeacherNoteField
          value={care.draft.teacher_note || ''}
          onChange={(teacher_note) => care.patch({ teacher_note })}
          error={care.errors.teacher_note}
        />
      ) : null}
      {care.draft ? (
        <CareRequestFields
          draft={care.draft}
          errors={care.errors}
          onChange={care.patch}
          initiatedByFixed="TEACHER"
          requestedLabel="Teacher request time (IST)"
        />
      ) : null}
      {care.retry ? (
        <CareSaveRetry
          message={care.retry.message}
          busy={care.retry.busy}
          onRetry={care.retry_save}
          onClose={props.on_close}
        />
      ) : (
        <Button data-session_id={change_session_uuid} disabled={!!busy} onClick={submit}>
          {busy ? 'Submitting…' : 'Submit'}
        </Button>
      )}
    </>
  );
}

ChangeTeacherForm.propTypes = {
  session_uuid: PropTypes.string,
  change_session_uuid: PropTypes.string,
  session_type: PropTypes.string,
  teachers: PropTypes.arrayOf(PropTypes.object).isRequired,
  warning: PropTypes.string,
  gender_caption: PropTypes.string,
  on_done: PropTypes.func.isRequired,
  on_close: PropTypes.func.isRequired,
  on_care_saved: PropTypes.func.isRequired,
  on_retry_change: PropTypes.func.isRequired,
  notify: PropTypes.func.isRequired
};

ChangeTeacherForm.defaultProps = {
  session_uuid: '',
  change_session_uuid: '',
  session_type: '',
  warning: '',
  gender_caption: ''
};

export default ChangeTeacherForm;
